
import React from 'react';
import { Bell, Check, CheckCheck, Trash2, ExternalLink, Sparkles } from 'lucide-react';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { ScrollArea } from '@/components/ui/scroll-area';
import { useNotifications } from '@/contexts/NotificationContext';
import { formatDistanceToNow } from 'date-fns';
import { Link } from 'react-router-dom';

const NotificationDropdown = () => {
  const {
    notifications,
    unreadCount,
    markAsRead,
    markAllAsRead,
    clearNotification,
    clearAllNotifications,
  } = useNotifications();

  const getNotificationIcon = (type: string) => {
    switch (type) {
      case 'success':
        return '🎉';
      case 'error':
        return '❌';
      case 'warning':
        return '⚠️';
      default:
        return 'ℹ️';
    }
  };

  const getNotificationColor = (type: string) => {
    switch (type) {
      case 'success':
        return 'border-l-green-500 bg-green-500/10 dark:bg-green-500/5';
      case 'error':
        return 'border-l-red-500 bg-red-500/10 dark:bg-red-500/5';
      case 'warning':
        return 'border-l-yellow-500 bg-yellow-500/10 dark:bg-yellow-500/5';
      default:
        return 'border-l-blue-500 bg-blue-500/10 dark:bg-blue-500/5';
    }
  };

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button variant="ghost" size="icon" className="relative hover:bg-accent/50 transition-all duration-200">
          <Bell className="w-5 h-5" />
          {unreadCount > 0 && (
            <Badge 
              variant="destructive" 
              className="absolute -top-2 -right-2 h-6 w-6 flex items-center justify-center p-0 text-xs font-bold bg-genova-gradient border-2 border-background animate-pulse"
            >
              {unreadCount > 99 ? '99+' : unreadCount}
            </Badge>
          )}
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent className="w-80 max-w-80 genova-card border-border/50 shadow-2xl" align="end">
        <div className="flex items-center justify-between p-4 border-b border-border/50 bg-gradient-to-r from-genova-purple/10 to-genova-blue/10">
          <div className="flex items-center space-x-3">
            <div className="w-8 h-8 bg-genova-gradient rounded-lg flex items-center justify-center">
              <Bell className="w-4 h-4 text-white" />
            </div>
            <div>
              <span className="font-bold text-card-foreground">Notifications</span>
              {unreadCount > 0 && (
                <Badge variant="secondary" className="ml-2 text-xs bg-genova-gradient text-white">
                  {unreadCount} new
                </Badge>
              )}
            </div>
          </div>
          {notifications.length > 0 && (
            <div className="flex space-x-1">
              {unreadCount > 0 && (
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={markAllAsRead}
                  className="h-8 w-8 p-0 hover:bg-accent/50"
                  title="Mark all as read"
                >
                  <CheckCheck className="w-4 h-4" />
                </Button>
              )}
              <Button
                variant="ghost"
                size="sm"
                onClick={clearAllNotifications}
                className="h-8 w-8 p-0 hover:bg-accent/50 text-muted-foreground"
                title="Clear all"
              >
                <Trash2 className="w-4 h-4" />
              </Button>
            </div>
          )}
        </div>

        <ScrollArea className="max-h-96">
          {notifications.length === 0 ? (
            <div className="p-8 text-center text-muted-foreground">
              <div className="w-16 h-16 mx-auto mb-4 bg-gradient-to-br from-genova-purple/20 to-genova-blue/20 rounded-full flex items-center justify-center">
                <Sparkles className="w-8 h-8 text-genova-purple/50" />
              </div>
              <p className="text-sm font-medium">All caught up!</p>
              <p className="text-xs">No new notifications</p>
            </div>
          ) : (
            <div className="space-y-1 p-2">
              {notifications.map((notification) => (
                <div
                  key={notification.id}
                  className={`relative p-4 rounded-lg border-l-4 transition-all hover:bg-accent/30 ${
                    getNotificationColor(notification.type)
                  } ${
                    !notification.read ? 'ring-2 ring-genova-purple/20' : 'bg-transparent'
                  }`}
                >
                  <div className="flex items-start justify-between space-x-3">
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center space-x-2 mb-2">
                        <span className="text-lg">{getNotificationIcon(notification.type)}</span>
                        <p className="text-sm font-semibold text-card-foreground truncate">
                          {notification.title}
                        </p>
                        {!notification.read && (
                          <div className="w-2 h-2 bg-genova-gradient rounded-full flex-shrink-0 animate-pulse" />
                        )}
                      </div>
                      <p className="text-xs text-muted-foreground mb-3 line-clamp-2 leading-relaxed">
                        {notification.message}
                      </p>
                      <div className="flex items-center justify-between">
                        <span className="text-xs text-muted-foreground font-medium">
                          {formatDistanceToNow(notification.createdAt, { addSuffix: true })}
                        </span>
                        <div className="flex items-center space-x-1">
                          {notification.actionUrl && (
                            <Link to={notification.actionUrl}>
                              <Button
                                variant="ghost"
                                size="sm"
                                className="h-7 w-7 p-0 hover:bg-accent/50"
                                title="View details"
                              >
                                <ExternalLink className="w-3 h-3" />
                              </Button>
                            </Link>
                          )}
                          {!notification.read && (
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => markAsRead(notification.id)}
                              className="h-7 w-7 p-0 hover:bg-accent/50"
                              title="Mark as read"
                            >
                              <Check className="w-3 h-3" />
                            </Button>
                          )}
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => clearNotification(notification.id)}
                            className="h-7 w-7 p-0 hover:bg-accent/50 text-muted-foreground"
                            title="Remove"
                          >
                            <Trash2 className="w-3 h-3" />
                          </Button>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </ScrollArea>

        {notifications.length > 3 && (
          <>
            <DropdownMenuSeparator />
            <div className="p-2">
              <Button variant="ghost" className="w-full text-xs font-medium hover:bg-accent/50" asChild>
                <Link to="/dashboard">
                  <Sparkles className="w-4 h-4 mr-2" />
                  View all notifications
                </Link>
              </Button>
            </div>
          </>
        )}
      </DropdownMenuContent>
    </DropdownMenu>
  );
};

export default NotificationDropdown;
